/**
 * O parecer que uma PESSOA escreveu, ao lado do que o sistema concluiu.
 *
 * O VALOR ESTA NO CRUZAMENTO, e nao em guardar um arquivo. Duas analises que
 * concordam dao confianca; duas que discordam sao o achado mais valioso do
 * sistema, porque e ali que se descobre que o prompt erra, que o edital tinha
 * uma exigencia que a leitura nao pegou, ou que a pessoa viu o que a maquina
 * nao ve. Por isso esta secao SEMPRE mostra os dois lados — inclusive quando
 * um deles nao existe, dizendo qual falta.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type LicitacaoDetalhe } from "../api/cliente";
import { useSessao } from "../sessao";
import { dia } from "../formato";
import { Secao } from "./Secao";
import css from "./AnaliseHumana.module.css";

/** O que o SISTEMA concluiu, na mesma seção, para a comparação ser possível
 *  sem rolar a página. Tres estados, e o terceiro e o que importa: "ninguem
 *  leu" nao pode virar espaco vazio. */
function LadoDoSistema({ d }: { d: LicitacaoDetalhe }) {
  if (d.analise_sintese) {
    return (
      <div className={css.lado}>
        <span className={css.quem}>
          {d.analise_leu_inteiro === false
            ? "O sistema leu o edital — NÃO INTEIRO"
            : "O sistema leu o edital inteiro"}
        </span>
        {d.analise_recomendacao ? (
          <b className={css.veredito}>{d.analise_recomendacao}</b>
        ) : null}
        <p>{d.analise_sintese}</p>
      </div>
    );
  }
  if (d.justificativa) {
    return (
      <div className={css.lado}>
        <span className={css.quem}>O sistema leu só o objeto</span>
        <p>{d.justificativa}</p>
        {/* Sem cifra e sem "espera aprovacao": a primeira e assunto
            interno e a segunda deixou de ser verdade na migracao 0061. */}
        <p className={css.nota}>
          A leitura do edital inteiro ainda não foi feita nesta licitação.
        </p>
      </div>
    );
  }
  return (
    <div className={css.lado}>
      <span className={css.quem}>O sistema ainda não avaliou esta</span>
    </div>
  );
}

/**
 * O envio do feedback, PARA SER MONTADO ONDE A PESSOA DECIDE.
 *
 * Saiu de dentro do painel de comparacao porque o botao que o chamava ficava
 * no topo e o formulario no fim da coluna: clicar em "Enviar feedback" rolava
 * a tela para longe da licitacao que se estava olhando. A acao tem de
 * acontecer onde foi pedida.
 *
 * Componente, e nao uma copia do formulario no outro lugar: duas copias do
 * mesmo `input type=file` divergiriam no dia em que uma delas ganhasse um
 * campo — e as duas escrevem na mesma fila.
 */
export function FormularioFeedback({ d }: { d: LicitacaoDetalhe }) {
  const { podeOperar } = useSessao();
  const qc = useQueryClient();
  const campo = useRef<HTMLInputElement>(null);
  const [veredito, setVeredito] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: ({ arquivo }: { arquivo: File }) =>
      api.enviarAnaliseHumana(d.id, arquivo, veredito),
    onSuccess: () => {
      setVeredito("");
      setErro(null);
      if (campo.current) campo.current.value = "";
      void qc.invalidateQueries({ queryKey: ["analise-humana", d.id] });
    },
    onError: (e: Error) => setErro(e.message),
  });

  // Quem nao opera nao ve formulario nenhum — a mesma regra de antes, e ela
  // mora aqui para que nenhum lugar que monte este componente precise lembrar.
  if (!podeOperar) return null;

  return (
      <div className={css.envio}>
        <label className={css.rotulo} htmlFor={`veredito-${d.id}`}>
          Sua conclusão, em uma frase (opcional)
        </label>
        <input
          id={`veredito-${d.id}`}
          value={veredito}
          onChange={(e) => setVeredito(e.currentTarget.value)}
          placeholder="ex.: NO-GO — exige registro no CREA"
          disabled={enviar.isPending}
        />
        <input
          ref={campo}
          type="file"
          accept=".pdf,.txt"
          disabled={enviar.isPending}
          onChange={(e) => {
            const arquivo = e.currentTarget.files?.[0];
            if (arquivo) enviar.mutate({ arquivo });
          }}
        />
        <p className={css.nota}>
          PDF ou .txt, até 25 MB. O texto é extraído e guardado; o arquivo
          original não fica. Enviar de novo não sobrescreve — grava outra
          análise, e as duas ficam.
        </p>
        {enviar.isPending && <p className={css.nota}>Lendo o arquivo…</p>}
        {/* PDF digitalizado sem camada de texto e o caso real aqui: os 192
            atestados do acervo sao todos assim. O 422 do servidor ja vem
            com a instrucao, entao basta mostra-lo. */}
        {erro && <p className={css.erro}>{erro}</p>}
      </div>
  );
}


export function PainelAnaliseHumana({ d }: { d: LicitacaoDetalhe }) {
  // `podeOperar` vem da sessão e não é recalculado aqui: `sessao.tsx` já
  // define quem opera (`papel === "operador"`), e uma segunda regra divergiria
  // dela no dia em que um papel novo aparecesse. O `tsc` pegou minha primeira
  // versão, que inventava um papel "admin" que não existe.
  const lista = useQuery({
    queryKey: ["analise-humana", d.id],
    queryFn: () => api.analisesHumanas(d.id),
  });

  const enviadas = lista.data ?? [];

  return (
    <Secao titulo="Análise humana × análise do sistema" conta={enviadas.length}>
      <>
        <LadoDoSistema d={d} />

        {enviadas.map((a) => (
          <div key={a.id} className={`${css.lado} ${css.humano}`}>
            <span className={css.quem}>
              {a.enviado_por} · {dia(a.enviado_em)}
            </span>
            {a.veredito ? <b className={css.veredito}>{a.veredito}</b> : null}
            {/* O texto inteiro, dobrado. Um resumo aqui obrigaria a confiar
                num resumo justamente onde se foi conferir o original. */}
            <details className={css.texto}>
              <summary>
                {a.arquivo}
                {a.paginas ? ` · ${a.paginas} pág.` : ""}
              </summary>
              <pre>{a.texto}</pre>
            </details>
          </div>
        ))}

        {!enviadas.length && (
          <p className={css.nota}>
            Ninguém enviou uma análise própria desta oportunidade ainda.
          </p>
        )}

      </>
    </Secao>
  );
}
